import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';

const MONO = "'JetBrains Mono', monospace";
const ORBITRON = "'Orbitron', monospace";
const TEAL = '#00FFE5';
const TEAL_DIM = 'rgba(0,255,229,0.6)';
const TEAL_FAINT = 'rgba(0,255,229,0.08)';
const TEAL_BORDER = 'rgba(0,255,229,0.2)';
const BG = '#080810';
const CARD_BG = 'rgba(13,13,26,0.95)';
const MUTED = 'rgba(0,255,229,0.45)';
const WHITE = '#E8E8F0';
const GREEN = '#2DC896';
const ORANGE = '#FF9500';
const RED = '#FF4444';


function SectionHeader({ title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
      <div style={{ width: 3, height: 20, background: TEAL, borderRadius: 2 }} />
      <span style={{ fontFamily: ORBITRON, fontSize: '0.85rem', fontWeight: 700, color: TEAL, letterSpacing: '0.15em' }}>{title}</span>
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '16px 20px', flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: ORBITRON, fontSize: '1.4rem', fontWeight: 700, color: color || TEAL }}>{value ?? '—'}</div>
    </div>
  );
}

function WorkspacePage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { show: showUpgradeModal } = useUpgradeModal();

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);

  const [removingId, setRemovingId] = useState(null);

  // FIX 3: cancel invite state
  const [cancelingInviteId, setCancelingInviteId] = useState(null);

  // ─── TOKEN ALLOCATION STATE ───
  const [allocations, setAllocations] = useState({});
  const [allocData, setAllocData] = useState(null);
  const [allocLoading, setAllocLoading] = useState(false);
  const [allocError, setAllocError] = useState(null);
  const [savingAlloc, setSavingAlloc] = useState(false);
  const [allocSaveMsg, setAllocSaveMsg] = useState(null);

  const getJwt = useCallback(async () => {
    return session?.access_token || null;
  }, [session?.access_token]);

  const fetchWorkspace = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getJwt();
      if (!token) {
        setError('Not authenticated.');
        setLoading(false);
        return;
      }
      const res = await fetch(apiUrl('/api/workspace'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.status === 403) {
        showUpgradeModal?.();
        setLoading(false);
        return;
      }
      if (!res?.ok) {
        setError(`Error ${res?.status}: ${res?.statusText}`);
        setLoading(false);
        return;
      }
      const data = await res?.json();
      setWorkspace(data);
    } catch (err) {
      setError('Failed to load workspace data.');
    } finally {
      setLoading(false);
    }
  }, [getJwt, showUpgradeModal]);

  // ─── FETCH ALLOCATIONS ───
  const fetchAllocations = useCallback(async () => {
    setAllocLoading(true);
    setAllocError(null);
    try {
      const token = await getJwt();
      if (!token) { setAllocLoading(false); return; }
      const res = await fetch(apiUrl('/api/workspace/allocations'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.status === 403) { showUpgradeModal?.(); setAllocLoading(false); return; }
      if (!res?.ok) { setAllocError(`Error ${res?.status}`); setAllocLoading(false); return; }
      const data = await res?.json();
      setAllocData(data);
      // Seed local allocation inputs — keyed by user_id (matches backend)
      const seed = {};
      (data?.allocations || [])?.forEach(a => {
        seed[a?.user_id] = a?.tokens_allocated ?? a?.tokens ?? 0;
      });
      setAllocations(seed);
    } catch (err) {
      setAllocError('Failed to load allocations.');
    } finally {
      setAllocLoading(false);
    }
  }, [getJwt, showUpgradeModal]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  const handleInvite = async (e) => {
    e?.preventDefault();
    if (!inviteEmail?.trim()) return;
    setInviting(true);
    setInviteMsg(null);
    try {
      const token = await getJwt();
      const res = await fetch(apiUrl('/api/workspace/invite'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail?.trim() }),
      });
      if (res?.status === 403) {
        showUpgradeModal?.();
        return;
      }
      if (!res?.ok) {
        const body = await res?.json()?.catch(() => ({}));
        setInviteMsg({ type: 'error', text: body?.message || `Error ${res?.status}` });
        return;
      }
      setInviteMsg({ type: 'success', text: `Invite sent to ${inviteEmail?.trim()}` });
      setInviteEmail('');
      fetchWorkspace();
    } catch (err) {
      setInviteMsg({ type: 'error', text: 'Failed to send invite.' });
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!userId) return;
    setRemovingId(userId);
    try {
      const token = await getJwt();
      const res = await fetch(apiUrl(`/api/workspace/member/${userId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.status === 403) {
        showUpgradeModal?.();
        return;
      }
      if (!res?.ok) {
        console.error('Remove member failed:', res?.status);
        return;
      }
      fetchWorkspace();
    } catch (err) {
      console.error('Remove member error:', err);
    } finally {
      setRemovingId(null);
    }
  };

  // FIX 3: cancel pending invite handler
  const handleCancelInvite = async (inviteId) => {
    if (!inviteId) return;
    setCancelingInviteId(inviteId);
    try {
      const token = await getJwt();
      const res = await fetch(apiUrl(`/api/workspace/invite/${inviteId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.status === 403) { showUpgradeModal?.(); return; }
      if (res?.ok) fetchWorkspace();
    } catch (err) {
      console.error('Cancel invite error:', err);
    } finally {
      setCancelingInviteId(null);
    }
  };

  const handleSaveAllocations = async () => {
    setSavingAlloc(true);
    setAllocSaveMsg(null);
    try {
      const token = await getJwt();
      const payload = {
        // FIX 1+2: user_id is already the correct key since uid = member?.user_id || member?.id
        allocations: Object.entries(allocations)?.map(([user_id, tokens]) => ({
          user_id,
          tokens: Number(tokens) || 0,
        })),
      };
      const res = await fetch(apiUrl('/api/workspace/allocations'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (res?.status === 403) { showUpgradeModal?.(); return; }
      if (!res?.ok) {
        const body = await res?.json()?.catch(() => ({}));
        setAllocSaveMsg({ type: 'error', text: body?.message || `Error ${res?.status}` });
        return;
      }
      setAllocSaveMsg({ type: 'success', text: 'Allocations saved successfully.' });
      fetchAllocations();
    } catch (err) {
      setAllocSaveMsg({ type: 'error', text: 'Failed to save allocations.' });
    } finally {
      setSavingAlloc(false);
    }
  };

  const seatsUsed = workspace?.seats_used ?? workspace?.members?.length ?? 0;
  const totalSeats = workspace?.workspace?.seats_limit ?? workspace?.total_seats ?? 5;
  const isWorkspaceOwner = workspace?.is_owner === true;
  const seatPct = Math.min((seatsUsed / totalSeats) * 100, 100);

  const totalPool = allocData?.total_tokens ?? allocData?.token_pool ?? 0;
  const totalAllocated = Object.values(allocations)?.reduce((sum, v) => sum + (Number(v) || 0), 0);
  const unallocated = totalPool - totalAllocated;
  const allocPct = totalPool > 0 ? Math.min((totalAllocated / totalPool) * 100, 100) : 0;
  const isOverAllocated = totalAllocated > totalPool;

  return (
    <div style={{ minHeight: '100vh', background: BG, color: WHITE }}>
      {/* Top Bar */}
      <div style={{ borderBottom: `1px solid ${TEAL_BORDER}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(8,8,16,0.95)', position: 'sticky', top: 0, zIndex: 50 }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          ← RECALLCORE™
        </button>
        <div style={{ fontFamily: ORBITRON, fontSize: 14, fontWeight: 700, color: '#FFD700', letterSpacing: '0.1em' }}>WORKSPACE</div>
        <div style={{ display: 'flex', gap: 16 }}>
          <button onClick={() => navigate('/lead-command-center')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em' }}>LEADS</button>
          <button onClick={() => navigate('/operations')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em' }}>OPERATIONS</button>
          <button onClick={() => navigate('/delivery-command-center')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: TEAL, letterSpacing: '0.1em' }}>DELIVERY</button>
        </div>
      </div>

      {/* Page Header */}
      <div style={{ padding: '32px 24px 0', maxWidth: 1000, margin: '0 auto' }}>
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontFamily: ORBITRON, fontSize: '1.4rem', fontWeight: 700, color: WHITE, letterSpacing: '0.08em' }}>TEAM WORKSPACE</span>
        </div>
        <div style={{ fontFamily: MONO, fontSize: '0.75rem', color: MUTED, letterSpacing: '0.1em', marginBottom: 32 }}>Manage members · Invite collaborators · Track execution</div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 60px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, fontFamily: MONO, fontSize: '0.75rem', color: MUTED, letterSpacing: '0.1em' }}>
            LOADING WORKSPACE DATA...
          </div>
        ) : error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, fontFamily: MONO, fontSize: '0.75rem', color: RED, letterSpacing: '0.1em' }}>
            {error}
          </div>
        ) : workspace ? (
          <>
            {/* ─── SECTION 1: OVERVIEW ─── */}
            <div style={{ marginBottom: 40 }}>
              <SectionHeader title="WORKSPACE OVERVIEW" />
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <MetricCard label="WORKSPACE NAME" value={workspace?.workspace?.name || workspace?.name || '—'} color={WHITE} />
                <MetricCard label="SEATS USED" value={`${seatsUsed} / ${totalSeats}`} color={seatsUsed >= totalSeats ? RED : TEAL} />
                <MetricCard label="MISSIONS THIS MONTH" value={workspace?.total_missions_this_month ?? workspace?.missions_this_month ?? '—'} color={GREEN} />
              </div>

              {/* Seat usage bar */}
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>SEAT CAPACITY</span>
                  <span style={{ fontFamily: MONO, fontSize: '0.6rem', color: seatsUsed >= totalSeats ? RED : TEAL, letterSpacing: '0.1em' }}>{seatsUsed}/{totalSeats} USED</span>
                </div>
                <div style={{ height: 4, background: 'rgba(0,255,229,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${seatPct}%`, background: seatsUsed >= totalSeats ? RED : TEAL, borderRadius: 2, transition: 'width 0.4s ease', boxShadow: `0 0 8px ${seatsUsed >= totalSeats ? RED : TEAL}` }} />
                </div>
              </div>
            </div>

            {/* ─── SECTION 2: ACTIVE MEMBERS ─── */}
            <div style={{ marginBottom: 40 }}>
              <SectionHeader title="ACTIVE MEMBERS" />
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 120px 100px', gap: 12, fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
                  <span>EMAIL</span>
                  <span>MISSIONS USED</span>
                  <span></span>
                </div>
                {workspace?.members?.length > 0 ? (
                  workspace?.members?.map((member) => (
                    <div
                      key={member?.id || member?.user_id || member?.email}
                      style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 120px 100px', gap: 12, alignItems: 'center' }}
                    >
                      <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {member?.email || '—'}
                      </span>
                      <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: TEAL }}>
                        {workspace?.usage?.[member?.user_id] ?? 0}
                      </span>
                      {isWorkspaceOwner ? <button
                        onClick={() => handleRemove(member?.user_id || member?.id)}
                        disabled={removingId === (member?.user_id || member?.id)}
                        style={{
                          background: 'rgba(255,68,68,0.1)',
                          border: '1px solid rgba(255,68,68,0.3)',
                          borderRadius: 3,
                          cursor: removingId === (member?.user_id || member?.id) ? 'not-allowed' : 'pointer',
                          fontFamily: MONO,
                          fontSize: '0.6rem',
                          color: RED,
                          letterSpacing: '0.08em',
                          padding: '4px 10px',
                          opacity: removingId === (member?.user_id || member?.id) ? 0.5 : 1,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { if (removingId !== (member?.user_id || member?.id)) e.currentTarget.style.background = 'rgba(255,68,68,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,68,68,0.1)'; }}
                      >
                        {removingId === (member?.user_id || member?.id) ? 'REMOVING...' : 'REMOVE'}
                      </button> : <span />}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px 16px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>No active members found.</div>
                )}
              </div>
            </div>

            {/* ─── SECTION 3: TOKEN ALLOCATION ─── */}
            <div style={{ marginBottom: 40 }}>
              <SectionHeader title="TOKEN ALLOCATION" />
              {allocLoading ? (
                <div style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, letterSpacing: '0.1em', padding: '20px 0' }}>LOADING ALLOCATION DATA...</div>
              ) : allocError ? (
                <div style={{ fontFamily: MONO, fontSize: '0.7rem', color: RED, letterSpacing: '0.1em', padding: '20px 0' }}>{allocError}</div>
              ) : (
                <>
                  {/* Pool summary */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '14px 18px', flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 6 }}>TOTAL TOKEN POOL</div>
                      <div style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: TEAL }}>{totalPool?.toLocaleString()}</div>
                    </div>
                    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '14px 18px', flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 6 }}>ALLOCATED</div>
                      <div style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: isOverAllocated ? RED : GREEN }}>{totalAllocated?.toLocaleString()}</div>
                    </div>
                    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '14px 18px', flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 6 }}>UNALLOCATED</div>
                      <div style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: isOverAllocated ? RED : WHITE }}>{unallocated?.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Live progress bar */}
                  <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '14px 18px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>ALLOCATION USAGE</span>
                      <span style={{ fontFamily: MONO, fontSize: '0.6rem', color: isOverAllocated ? RED : TEAL, letterSpacing: '0.1em' }}>
                        {totalAllocated?.toLocaleString()} / {totalPool?.toLocaleString()} {isOverAllocated ? '⚠ OVER LIMIT' : ''}
                      </span>
                    </div>
                    <div style={{ height: 4, background: 'rgba(0,255,229,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${allocPct}%`,
                        background: isOverAllocated ? RED : TEAL,
                        borderRadius: 2,
                        transition: 'width 0.2s ease, background 0.2s ease',
                        boxShadow: `0 0 8px ${isOverAllocated ? RED : TEAL}`,
                      }} />
                    </div>
                  </div>

                  {/* Per-member allocation inputs */}
                  <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
                    <div style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12, fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
                      <span>MEMBER EMAIL</span>
                      <span>TOKEN ALLOCATION</span>
                    </div>
                    {workspace?.members?.length > 0 ? (
                      workspace?.members?.map((member) => {
                        // FIX 1+2: prioritize user_id so allocation keys match backend storage
                        const uid = member?.user_id || member?.id;
                        return (
                          <div
                            key={uid || member?.email}
                            style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12, alignItems: 'center' }}
                          >
                            <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {member?.email || '—'}
                            </span>
                            <input
                              type="number"
                              disabled={!isWorkspaceOwner}
                              min="0"
                              max={totalPool}
                              value={allocations?.[uid] ?? 0}
                              onChange={e => {
                                const val = Math.max(0, parseInt(e?.target?.value, 10) || 0);
                                setAllocations(prev => ({ ...prev, [uid]: val }));
                                setAllocSaveMsg(null);
                              }}
                              style={{
                                width: '100%',
                                background: 'rgba(0,255,229,0.04)',
                                border: `1px solid ${TEAL_BORDER}`,
                                borderRadius: 4,
                                padding: '6px 10px',
                                fontFamily: MONO,
                                fontSize: '0.75rem',
                                color: TEAL,
                                outline: 'none',
                                letterSpacing: '0.05em',
                                boxSizing: 'border-box',
                                textAlign: 'right',
                                cursor: isWorkspaceOwner ? 'text' : 'not-allowed',
                                opacity: isWorkspaceOwner ? 1 : 0.7,
                              }}
                              onFocus={e => { e.target.style.borderColor = TEAL; e.target.style.boxShadow = `0 0 0 1px ${TEAL}`; }}
                              onBlur={e => { e.target.style.borderColor = TEAL_BORDER; e.target.style.boxShadow = 'none'; }}
                            />
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '20px 16px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>No active members to allocate tokens to.</div>
                    )}
                  </div>

                  {/* Save button */}
                  {isWorkspaceOwner && <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <button
                      onClick={handleSaveAllocations}
                      disabled={savingAlloc || isOverAllocated || workspace?.members?.length === 0}
                      style={{
                        background: savingAlloc || isOverAllocated ? TEAL_FAINT : 'rgba(0,255,229,0.12)',
                        border: `1px solid ${isOverAllocated ? RED : savingAlloc ? TEAL_BORDER : TEAL}`,
                        borderRadius: 4,
                        cursor: savingAlloc || isOverAllocated || workspace?.members?.length === 0 ? 'not-allowed' : 'pointer',
                        fontFamily: MONO,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        color: isOverAllocated ? RED : savingAlloc ? MUTED : TEAL,
                        letterSpacing: '0.12em',
                        padding: '10px 24px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                        opacity: workspace?.members?.length === 0 ? 0.4 : 1,
                      }}
                      onMouseEnter={e => { if (!savingAlloc && !isOverAllocated && workspace?.members?.length > 0) e.currentTarget.style.background = 'rgba(0,255,229,0.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = savingAlloc || isOverAllocated ? TEAL_FAINT : 'rgba(0,255,229,0.12)'; }}
                    >
                      {savingAlloc ? 'SAVING...' : 'SAVE ALLOCATIONS'}
                    </button>
                    {isOverAllocated && (
                      <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: RED, letterSpacing: '0.08em' }}>
                        ⚠ Total exceeds token pool by {(totalAllocated - totalPool)?.toLocaleString()} tokens
                      </span>
                    )}
                    {allocSaveMsg && (
                      <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: allocSaveMsg?.type === 'success' ? GREEN : RED, letterSpacing: '0.08em' }}>
                        {allocSaveMsg?.type === 'success' ? '✓ ' : '✗ '}{allocSaveMsg?.text}
                      </span>
                    )}
                  </div>}
                </>
              )}
            </div>

            {/* ─── SECTION 4: PENDING INVITES ─── */}
            <div style={{ marginBottom: 40, display: isWorkspaceOwner ? 'block' : 'none' }}>
              <SectionHeader title="PENDING INVITES" />
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden' }}>
                {/* FIX 3: added third column for CANCEL button */}
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 140px 90px', gap: 12, fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
                  <span>EMAIL</span>
                  <span>STATUS</span>
                  <span></span>
                </div>
                {workspace?.pending_invites?.length > 0 ? (
                  workspace?.pending_invites?.map((invite, idx) => (
                    <div
                      key={invite?.id || invite?.invited_email || idx}
                      style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 140px 90px', gap: 12, alignItems: 'center' }}
                    >
                      <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {invite?.invited_email || '—'}
                      </span>
                      <span style={{ fontFamily: MONO, fontSize: '0.6rem', letterSpacing: '0.08em', padding: '2px 8px', borderRadius: 3, background: 'rgba(255,149,0,0.12)', color: ORANGE, border: '1px solid rgba(255,149,0,0.3)', display: 'inline-block' }}>
                        PENDING
                      </span>
                      {/* FIX 3: cancel button */}
                      <button
                        onClick={() => handleCancelInvite(invite?.id)}
                        disabled={cancelingInviteId === invite?.id}
                        style={{
                          background: 'rgba(255,68,68,0.08)',
                          border: '1px solid rgba(255,68,68,0.25)',
                          borderRadius: 3,
                          cursor: cancelingInviteId === invite?.id ? 'not-allowed' : 'pointer',
                          fontFamily: MONO,
                          fontSize: '0.6rem',
                          color: RED,
                          letterSpacing: '0.08em',
                          padding: '3px 8px',
                          opacity: cancelingInviteId === invite?.id ? 0.5 : 1,
                          transition: 'all 0.15s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { if (cancelingInviteId !== invite?.id) e.currentTarget.style.background = 'rgba(255,68,68,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,68,68,0.08)'; }}
                      >
                        {cancelingInviteId === invite?.id ? '...' : 'CANCEL'}
                      </button>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px 16px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>No pending invites.</div>
                )}
              </div>
            </div>

            {/* ─── SECTION 5: INVITE FORM ─── */}
            <div style={{ marginBottom: 40, display: isWorkspaceOwner ? 'block' : 'none' }}>
              <SectionHeader title="INVITE MEMBER" />
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '24px' }}>
                <form onSubmit={handleInvite} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e?.target?.value)}
                      placeholder="member@company.com"
                      required
                      style={{
                        width: '100%',
                        background: 'rgba(0,255,229,0.04)',
                        border: `1px solid ${TEAL_BORDER}`,
                        borderRadius: 4,
                        padding: '10px 14px',
                        fontFamily: MONO,
                        fontSize: '0.75rem',
                        color: WHITE,
                        outline: 'none',
                        letterSpacing: '0.05em',
                        boxSizing: 'border-box',
                      }}
                      onFocus={e => { e.target.style.borderColor = TEAL; e.target.style.boxShadow = `0 0 0 1px ${TEAL}`; }}
                      onBlur={e => { e.target.style.borderColor = TEAL_BORDER; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={inviting || !inviteEmail?.trim()}
                    style={{
                      background: inviting ? TEAL_FAINT : 'rgba(0,255,229,0.12)',
                      border: `1px solid ${inviting ? TEAL_BORDER : TEAL}`,
                      borderRadius: 4,
                      cursor: inviting || !inviteEmail?.trim() ? 'not-allowed' : 'pointer',
                      fontFamily: MONO,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: inviting ? MUTED : TEAL,
                      letterSpacing: '0.12em',
                      padding: '10px 20px',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s',
                      opacity: !inviteEmail?.trim() ? 0.5 : 1,
                    }}
                    onMouseEnter={e => { if (!inviting && inviteEmail?.trim()) e.currentTarget.style.background = 'rgba(0,255,229,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = inviting ? TEAL_FAINT : 'rgba(0,255,229,0.12)'; }}
                  >
                    {inviting ? 'SENDING...' : 'INVITE MEMBER'}
                  </button>
                </form>
                {inviteMsg && (
                  <div style={{ marginTop: 12, fontFamily: MONO, fontSize: '0.7rem', color: inviteMsg?.type === 'success' ? GREEN : RED, letterSpacing: '0.08em' }}>
                    {inviteMsg?.type === 'success' ? '✓ ' : '✗ '}{inviteMsg?.text}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, fontFamily: MONO, fontSize: '0.75rem', color: MUTED, letterSpacing: '0.1em' }}>
            NO WORKSPACE DATA AVAILABLE.
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkspacePage;


